import React, { useState, useEffect } from 'react';
import { X, MessageSquare } from 'lucide-react';
import { logEvent } from "../../services/evalMode.js";
import { db, getUserPlanData, analytics } from "../../services/firebase";
import { Timestamp } from 'firebase/firestore';

interface FeedbackSurveyProps {
  googleFormUrl: string;
}

export default function FeedbackSurvey({ googleFormUrl }: FeedbackSurveyProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed the survey
    const surveyDismissed = localStorage.getItem('feedbackSurveyDismissed');
    if (surveyDismissed === 'true') {
      return;
    }

    // Get current visit count
    const currentVisits = parseInt(localStorage.getItem('myDesignsVisitCount') || '0');
    const newVisitCount = currentVisits + 1;
    
    // Update visit count
    localStorage.setItem('myDesignsVisitCount', newVisitCount.toString());

    // Show survey after # of visit with a small delay
    if (newVisitCount >= 10) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000); // 3 second delay

      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem('feedbackSurveyDismissed', 'true');
    logEvent(analytics, "dismiss_feedback_survey", {
     
        timestamp: Timestamp.now().toDate().toISOString(), // Log as ISO string for consistency
    
      });
  };

  const handleTakeSurvey = () => {
    // Open Google Form in new tab
    window.open(googleFormUrl, '_blank');

    logEvent(analytics, "click_feedback_survey", {
     
        timestamp: Timestamp.now().toDate().toISOString(), // Log as ISO string for consistency
    
      });
    // Mark as dismissed after taking survey
    handleDismiss();
  };

  if (!isVisible || isDismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-16 z-50 max-w-sm text-left">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 animate-slide-up">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Help make DrillFlo better!</h3>
              <p className="text-sm text-gray-600">We'd love to hear your feedback</p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <p className="text-sm text-gray-700 mb-4">
          Your input helps us improve DrillFlo for everyone. Take 3 minutes to share your thoughts and help shape the future of drill design.
        </p>
        
        <div className="flex space-x-3">
          <button
            onClick={handleTakeSurvey}
            className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Take Survey
          </button>
          <button
            onClick={handleDismiss}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
} 